import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useAdmin, type UserDetail } from '@/hooks/useAdmin'
import { useAuth } from '@/hooks/useAuth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  ArrowLeft,
  User,
  Shield,
  CheckCircle2,
  ShieldAlert,
  Building2,
  KeyRound,
  Calendar,
  Mail,
} from 'lucide-react'
import axios from 'axios'

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { getUser, updateUser, resetUserPassword } = useAdmin()
  const { user: currentUser } = useAuth()

  const [data, setData] = useState<UserDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Admin toggle confirm dialog
  const [adminConfirmOpen, setAdminConfirmOpen] = useState(false)
  const [pendingAdminValue, setPendingAdminValue] = useState(false)
  const [adminBusy, setAdminBusy] = useState(false)

  // Password reset
  const [resetBusy, setResetBusy] = useState(false)

  const load = () => {
    if (!id) return
    setLoading(true)
    setError(null)
    getUser(id)
      .then((user) => setData(user))
      .catch(() => setError('Failed to load user'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleAdminSwitchChange = (checked: boolean) => {
    setPendingAdminValue(checked)
    setAdminConfirmOpen(true)
  }

  const handleConfirmAdmin = async () => {
    if (!id) return
    setAdminBusy(true)
    try {
      await updateUser(id, { isPlatformAdmin: pendingAdminValue })
      toast.success(
        pendingAdminValue ? 'Platform admin granted' : 'Platform admin revoked',
      )
      setAdminConfirmOpen(false)
      load()
    } catch (err) {
      // Catch the 400 self-lockout from the server
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        const msg =
          err.response?.data?.message ||
          'Cannot remove your own platform-admin access'
        toast.error(msg)
      } else {
        toast.error('Failed to update user')
      }
      setAdminConfirmOpen(false)
    } finally {
      setAdminBusy(false)
    }
  }

  const handlePasswordReset = async () => {
    if (!id) return
    setResetBusy(true)
    try {
      await resetUserPassword(id)
      toast.success('Password reset email sent')
    } catch {
      toast.error('Failed to send password reset email')
    } finally {
      setResetBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-5 w-20 rounded bg-muted/40 animate-pulse" />
        <div className="h-8 w-56 rounded bg-muted/40 animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[0, 1].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-4 w-32 rounded bg-muted/40 animate-pulse mb-3" />
                <div className="h-16 rounded bg-muted/30 animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="space-y-4">
        <Link
          to="/users"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Users
        </Link>
        <Card>
          <CardContent className="p-6 text-destructive text-sm">
            {error || 'User not found'}
          </CardContent>
        </Card>
      </div>
    )
  }

  const isSelf = currentUser?.id === data.id || currentUser?.email === data.email

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/users"
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Users
      </Link>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="brand-wordmark text-2xl">{data.name || data.email}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="mono-accent text-muted-foreground">{data.email}</span>
              {data.is_platform_admin && (
                <span className="status-pill status-pill-danger flex items-center gap-1">
                  <Shield className="h-3 w-3" />
                  Admin
                </span>
              )}
              {data.email_verified && (
                <span className="status-pill status-pill-success flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Verified
                </span>
              )}
              {data.mfa_enabled && (
                <span className="status-pill status-pill-muted flex items-center gap-1">
                  <ShieldAlert className="h-3 w-3" />
                  MFA
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePasswordReset}
            disabled={resetBusy}
          >
            <KeyRound className="h-3.5 w-3.5 mr-1.5" />
            {resetBusy ? 'Sending…' : 'Send password reset'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Profile card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3">
              <div className="flex items-start gap-2">
                <Mail className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <dt className="mono-accent text-muted-foreground">Email</dt>
                  <dd className="text-sm">{data.email}</dd>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <User className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <dt className="mono-accent text-muted-foreground">Name</dt>
                  <dd className="text-sm">{data.name || '—'}</dd>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <dt className="mono-accent text-muted-foreground">Created</dt>
                  <dd className="text-sm">{new Date(data.created_at).toLocaleString()}</dd>
                </div>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* Access card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Shield className="h-4 w-4 text-muted-foreground" />
              Access
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="platform-admin-switch" className="text-sm font-medium">
                  Platform admin
                </Label>
                <p className="text-xs text-muted-foreground">
                  {isSelf
                    ? 'You cannot change your own platform-admin access'
                    : 'Full access to admin panel and all organizations'}
                </p>
              </div>
              <Switch
                id="platform-admin-switch"
                checked={data.is_platform_admin}
                onCheckedChange={handleAdminSwitchChange}
                disabled={isSelf}
              />
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-border">
              <div className="space-y-0.5">
                <p className="text-sm font-medium">Email verified</p>
                <p className="text-xs text-muted-foreground">
                  {data.email_verified ? 'Verified' : 'Not verified'}
                </p>
              </div>
              <CheckCircle2
                className={`h-4 w-4 ${data.email_verified ? 'text-success' : 'text-muted-foreground/40'}`}
              />
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-border">
              <div className="space-y-0.5">
                <p className="text-sm font-medium">MFA</p>
                <p className="text-xs text-muted-foreground">
                  {data.mfa_enabled ? 'Enabled' : 'Not enabled'}
                </p>
              </div>
              <ShieldAlert
                className={`h-4 w-4 ${data.mfa_enabled ? 'text-primary' : 'text-muted-foreground/40'}`}
              />
            </div>
          </CardContent>
        </Card>

        {/* Memberships */}
        {data.memberships.length > 0 && (
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                Organization memberships ({data.memberships.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {/* Header */}
              <div className="grid grid-cols-[1fr_120px] gap-4 px-6 py-2.5 border-b text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <span>Organization</span>
                <span>Role</span>
              </div>
              <div className="divide-y divide-border">
                {data.memberships.map((m) => (
                  <div
                    key={m.organization_id}
                    className="grid grid-cols-[1fr_120px] gap-4 px-6 py-3 items-center"
                  >
                    <div className="min-w-0">
                      <Link
                        to={`/orgs/${m.organization_id}`}
                        className="text-sm font-medium hover:text-primary transition-colors truncate block"
                      >
                        {m.org_name}
                      </Link>
                      <p className="mono-accent text-muted-foreground truncate">
                        {m.organization_id}
                      </p>
                    </div>
                    <span className="status-pill status-pill-muted capitalize">{m.role}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Confirm admin toggle dialog */}
      <Dialog open={adminConfirmOpen} onOpenChange={setAdminConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {pendingAdminValue ? 'Grant platform admin?' : 'Revoke platform admin?'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 text-sm text-muted-foreground space-y-2">
            {pendingAdminValue ? (
              <p>
                This will give <span className="font-medium text-foreground">{data.email}</span>{' '}
                full access to the admin panel and all organizations.
              </p>
            ) : (
              <p>
                This will remove platform-admin access for{' '}
                <span className="font-medium text-foreground">{data.email}</span>.
              </p>
            )}
            {isSelf && !pendingAdminValue && (
              <p className="text-destructive font-medium">
                Warning: this is your own account. You will lose admin access.
              </p>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" size="sm">Cancel</Button>
            </DialogClose>
            <Button
              size="sm"
              variant={pendingAdminValue ? 'default' : 'destructive'}
              disabled={adminBusy}
              onClick={handleConfirmAdmin}
            >
              {adminBusy ? 'Saving…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
